import {
  Badge,
  Box,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  usePrevious,
} from "@chakra-ui/react";
import { Decimal } from "decimal.js";
import { CreditFacilitySubpageProps } from "features/credit-facility/CreditFacilitySubpageProps";
import { getDefaultNewUse } from "features/credit-facility/reallocation/CreditFacilityReallocationSplit";
import {
  FormDrawdownAllocationInput,
  FormDrawdownFundAmountInput,
  FormDrawdownTotalAmountInput,
} from "features/credit-facility/reallocation/ReallocationFormFields";
import { useCreditFacilityReallocationCalculator } from "features/credit-facility/reallocation/useCreditFacilityReallocationCalculator";
import { useGetAllocationsByLookupKey } from "features/drawdown/DrawdownUtils";
import { useCreditFacilityRetrieveSuspense } from "gen/endpoints/credit-facility/credit-facility";
import { useInvestmentListSuspense } from "gen/endpoints/investment/investment";
import { useTransactionCodeConfigListSuspense } from "gen/endpoints/transactions/transactions";
import { CallOrDistributionEnum } from "gen/model/callOrDistributionEnum";
import { CreditFacilityPrincipalUseReallocationDataRequest } from "gen/model/creditFacilityPrincipalUseReallocationDataRequest";
import { CreditFacilityPrincipalUseReallocationUpsertData } from "gen/model/creditFacilityPrincipalUseReallocationUpsertData";
import { CreditFacilityPrincipalUseTypeEnum } from "gen/model/creditFacilityPrincipalUseTypeEnum";
import { CreditFacilityReallocationExistingUseReadData } from "gen/model/creditFacilityReallocationExistingUseReadData";
import { CreditFacilityReallocationReadDataUsesItem } from "gen/model/creditFacilityReallocationReadDataUsesItem";
import { FundEntityReadData } from "gen/model/fundEntityReadData";
import { TransactionTypeEnum } from "gen/model/transactionTypeEnum";
import React, { useCallback, useContext, useEffect } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { FormHiddenInput } from "shared/components/form/FormHiddenInput";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import {
  TableAddButton,
  TableRemoveButton,
} from "shared/components/table/TableRowActionButtons";
import { FormErrorTooltip } from "shared/components/tooltip/ErrorTooltip";
import { tryParseDecimal } from "shared/utils/DecimalUtils";
import { enumKeys } from "shared/utils/EnumUtils";
import { setFormValueIfChanged } from "shared/utils/FormUtils";
import {
  formatMoney,
  formatMoneyFromDecimal,
  formatMoneyWithoutCurrencySymbol,
  sumMoneyStrings,
} from "shared/utils/MoneyUtils";

interface ReallocationTableProps {
  group: {
    group: CreditFacilityReallocationReadDataUsesItem;
    index: number;
  };
  funds: FundEntityReadData[];
}

type ReallocationTableContextType = {
  existingUse: CreditFacilityReallocationExistingUseReadData;
  funds: FundEntityReadData[];
  showTransactions: boolean;
};

const ReallocationTableContext =
  React.createContext<ReallocationTableContextType>({
    existingUse: null as never,
    funds: [],
    showTransactions: false,
  });

export const ReallocationTable: React.FC<ReallocationTableProps> = ({
  group,
  funds,
}) => {
  const { t } = useTranslation(["common", "creditFacility", "enums"]);
  const { fundFamilyId, creditFacilityId } =
    useOutletContext<CreditFacilitySubpageProps>();
  const { data: creditFacility } = useCreditFacilityRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
  );

  const { group: useGroup, index } = group;

  // for each group we can just grab the first use and get the name as every use in the group has the same name/investment
  const firstUseInGroup = Object.values(useGroup)[0];
  const name =
    firstUseInGroup.use_type == CreditFacilityPrincipalUseTypeEnum.investment
      ? firstUseInGroup.investment_name
      : t(
          `enums:CreditFacilityPrincipalUseTypeEnum.${firstUseInGroup.use_type}`,
        );
  return (
    <TableBox isFullWidth={true} boxProps={{ mb: "40px" }}>
      <TableBoxHeader>
        <TableBoxTitle>
          {t("creditFacility:reallocation.split.table.title", { name })}
        </TableBoxTitle>
      </TableBoxHeader>
      <TableBoxBody>
        <TableContainer height="100%" overflowY="auto">
          <Table variant="drawdown">
            <Thead>
              <Tr>
                <Th>
                  <Text pl="35px">{t("common:drawdown.useOfFunds")}</Text>
                </Th>
                <Th>{t("common:drawdown.autoAllocate")}</Th>
                <Th className="mb-drawdown-fund-family">
                  {t("common:drawdown.fundFamilyTotal")}
                </Th>
                {funds.map((fund) => (
                  <Th key={fund.id} className="mb-drawdown-fund">
                    <Box data-fund-title={true}>{fund.legal_name}</Box>
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {Object.entries(useGroup).map(([id, use]) => (
                <ReallocationTableContext.Provider
                  key={id}
                  value={{
                    existingUse: use,
                    funds,
                    showTransactions:
                      creditFacility.transaction_code_configs_available,
                  }}
                >
                  <ExistingUseRow />
                  <ReallocationUseRowTable
                    fundFamilyId={fundFamilyId}
                    rowId={`uses.${index}.${id}.new_uses`}
                  />
                  <TotalRowsForExistingUse
                    rowId={`uses.${index}.${id}.new_uses`}
                  />
                </ReallocationTableContext.Provider>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </TableBoxBody>
    </TableBox>
  );
};

const ExistingUseRow: React.FC = () => {
  const { t } = useTranslation(["common", "enums", "creditFacility"]);
  const { existingUse, showTransactions, funds } =
    useContext<ReallocationTableContextType>(ReallocationTableContext);
  return (
    <Tr backgroundColor="#F7FCF9">
      <Th backgroundColor="#F7FCF9 !important">
        <HStack width="auto" gap="8px" pl="35px">
          <HStack width="300px">
            <Text>
              {t(
                `enums:CreditFacilityPrincipalUseTypeEnum.${existingUse.use_type}`,
              )}
            </Text>
            <Badge colorScheme="dark_gray">{`${t("creditFacility:tranche")} ${
              existingUse.tranche_date
            }`}</Badge>
          </HStack>
          <Text width="300px">{existingUse.investment_name}</Text>
          {showTransactions && (
            <Text width="300px">
              {existingUse.transaction_code_config_name}
            </Text>
          )}
        </HStack>
      </Th>
      <Td className="mb-drawdown-allocation" />
      <Td>{formatMoneyWithoutCurrencySymbol(existingUse.amount)}</Td>
      {funds.map((fund) => (
        <Td key={fund.id}>
          {fund.id && formatMoney(existingUse.fund_amounts[fund.id].amount)}
        </Td>
      ))}
    </Tr>
  );
};

const TotalRowsForExistingUse: React.FC<{
  rowId: string;
}> = ({ rowId }) => {
  const { existingUse, funds } = useContext<ReallocationTableContextType>(
    ReallocationTableContext,
  );
  const { t } = useTranslation(["creditFacility"]);
  const newUseInputs = useWatch({
    control: useFormContext().control,
    name: rowId,
  }) as CreditFacilityPrincipalUseReallocationUpsertData[];
  const existing_fund_family_amount: Decimal =
    tryParseDecimal(existingUse.amount) ?? new Decimal(0);
  const new_fund_family_amount =
    tryParseDecimal(
      sumMoneyStrings(newUseInputs.map(({ amount }) => amount)),
    ) || new Decimal(0);
  const fund_family_amount_remaining = existing_fund_family_amount.minus(
    new_fund_family_amount,
  );
  const remaining_fund_amounts = funds.reduce<Record<string, Decimal>>(
    (reducer, fund) => {
      if (!fund.id) {
        return reducer;
      }
      const existing_fund_amount =
        tryParseDecimal(existingUse.fund_amounts[fund.id].amount) ??
        new Decimal(0);
      const new_fund_amount =
        tryParseDecimal(
          sumMoneyStrings(
            newUseInputs.map(
              ({ fund_amounts }) => fund_amounts[fund.id].amount,
            ),
          ),
        ) ?? new Decimal(0);
      const fund_amount_remaining = existing_fund_amount.minus(new_fund_amount);
      reducer[fund.id] = fund_amount_remaining;
      return reducer;
    },
    {},
  );
  return (
    <Tr border="10px solid gray.100">
      <Th>
        <Text>{t("creditFacility:reallocation.totalRemaining")}</Text>
      </Th>
      <Td />
      <Td>{formatMoneyFromDecimal(fund_family_amount_remaining)}</Td>
      {funds.map(
        (fund) =>
          fund.id && (
            <Td key={fund.id}>
              {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
              {remaining_fund_amounts[fund.id] &&
                formatMoneyFromDecimal(remaining_fund_amounts[fund.id])}
            </Td>
          ),
      )}
    </Tr>
  );
};

const ReallocationUseRowTable: React.FC<{
  fundFamilyId: string;
  rowId: string;
}> = ({ fundFamilyId, rowId }) => {
  const { existingUse, funds } = useContext<ReallocationTableContextType>(
    ReallocationTableContext,
  );
  const form =
    useFormContext<CreditFacilityPrincipalUseReallocationDataRequest>();
  const { fundFamilyEvent } = useOutletContext<CreditFacilitySubpageProps>();
  const name = rowId as "uses.0.id.new_uses";
  const watchedFields = useWatch({
    control: form.control,
    name,
  });
  const lookupKeys = watchedFields.map((field) => ({
    transaction_code_id: field.transaction_code_id || null,
    investment_id: field.investment_entity_id || null,
    fund_ids: funds.map((fund) => fund.id),
  }));
  const getAllocationsByLookupKey = useGetAllocationsByLookupKey(
    fundFamilyId,
    lookupKeys,
    fundFamilyEvent.effective_date,
    funds,
  );

  useCreditFacilityReallocationCalculator(
    form,
    getAllocationsByLookupKey!,
    rowId,
  );
  const {
    fields: rows,
    append,
    remove,
  } = useFieldArray({
    control: form.control,
    name,
  });

  const addRow = useCallback(() => {
    append(getDefaultNewUse(funds, existingUse));
  }, [append, existingUse, funds]);

  return rows.map((_, index) => (
    <TableRow
      row={{ id: rowId, index }}
      removeRow={remove}
      addRow={addRow}
      funds={funds}
      key={index}
    />
  ));
};

const TableRow: React.FC<{
  row: {
    id: string;
    index: number;
  };
  removeRow: (index: number) => void;
  addRow: () => void;
  funds: FundEntityReadData[];
}> = ({ row, removeRow, addRow, funds }) => (
  <Tr key={row.id}>
    <Th>
      <HStack spacing="8px">
        <TableAddButton onClick={() => addRow()} alwaysVisible={true} />
        <ReallocationInput formNamePrefix={`${row.id}.${row.index}`} />
        {row.index && (
          <TableRemoveButton onClick={() => removeRow(row.index)} />
        )}
      </HStack>
    </Th>
    <Td className="mb-drawdown-allocation">
      <FormDrawdownAllocationInput
        formNamePrefix={`${row.id}.${row.index}`}
        funds={funds}
      />
    </Td>
    <Td>
      <FormDrawdownTotalAmountInput
        formNamePrefix={`${row.id}.${row.index}`}
        funds={funds}
      />
    </Td>
    {funds.map((fund) => (
      <Td key={fund.id}>
        <FormDrawdownFundAmountInput
          fund={{
            id: fund.id,
            title: fund.legal_name,
          }}
          formNamePrefix={`${row.id}.${row.index}`}
          funds={funds}
        />
      </Td>
    ))}
  </Tr>
);

export const ReallocationInput: React.FC<{
  formNamePrefix: string;
}> = ({ formNamePrefix }) => {
  const { fundFamilyId } = useOutletContext<CreditFacilitySubpageProps>();
  const { showTransactions, existingUse } =
    useContext<ReallocationTableContextType>(ReallocationTableContext);
  const { data: transactionData } = useTransactionCodeConfigListSuspense(
    fundFamilyId,
    {
      call_or_distribution: CallOrDistributionEnum.drawdown,
      transaction_type: TransactionTypeEnum.portfolio,
    },
  );
  const { data: investmentData } = useInvestmentListSuspense(fundFamilyId, {
    is_allocable: true,
  });

  const form = useFormContext();

  const { t } = useTranslation([
    "common",
    "creditFacility",
    "capitalCall",
    "enums",
  ]);

  const txFieldName =
    `${formNamePrefix}.transaction_code_id` as "uses.0.transaction_code_id";

  const investmentFieldName =
    `${formNamePrefix}.investment_entity_id` as "uses.0.investment_entity_id";

  const useTypeIdFieldName = `${formNamePrefix}.use_type` as "uses.0.use_type";

  const useTypeId = useWatch({
    control: form.control,
    name: useTypeIdFieldName,
  }) as CreditFacilityPrincipalUseTypeEnum;

  const currentTransactionCode = useWatch({
    control: form.control,
    name: txFieldName,
  }) as string;

  // this only works right now as the default is always the first one in the list, we might need to revisit this if that ever changes.
  const defaultTransactionCode = transactionData.find(
    ({ classification }) => classification === useTypeId,
  )?.transaction_code_id;

  const uses = enumKeys(CreditFacilityPrincipalUseTypeEnum).filter(
    (key) =>
      existingUse.use_type == CreditFacilityPrincipalUseTypeEnum.investment ||
      key !== existingUse.use_type,
  );

  const previousUseTypeId = usePrevious<string | undefined>(useTypeId);
  const previousTransactionCode = usePrevious<string | undefined>(
    currentTransactionCode,
  );
  const updatedCode =
    previousTransactionCode !== currentTransactionCode
      ? currentTransactionCode
      : defaultTransactionCode;

  useEffect(() => {
    if (useTypeId !== previousUseTypeId) {
      setFormValueIfChanged(form, txFieldName, updatedCode);
      if (useTypeId !== "investment") {
        setFormValueIfChanged(form, investmentFieldName, undefined);
      }
    }
  }, [
    form,
    txFieldName,
    defaultTransactionCode,
    useTypeId,
    previousUseTypeId,
    investmentFieldName,
    currentTransactionCode,
    updatedCode,
  ]);

  return (
    <HStack spacing="8px" width="auto">
      <Box flex="1">
        <FormErrorTooltip
          name={useTypeIdFieldName}
          wrapperProps={{ flexGrow: 1 }}
        >
          <FormSelect
            title={t("creditFacility:reallocation.split.table.newUse")}
            name={useTypeIdFieldName}
            hideMessages={true}
            controlSx={{
              width: "300px",
            }}
          >
            {uses.map((useType) => (
              <SelectOption value={useType} key={useType}>
                {t(`enums:CreditFacilityPrincipalUseTypeEnum.${useType}`)}
              </SelectOption>
            ))}
          </FormSelect>
        </FormErrorTooltip>
      </Box>
      <Box flex="1">
        <FormErrorTooltip
          name={investmentFieldName}
          wrapperProps={{ flexGrow: 1 }}
        >
          <FormSelect
            title={t("common:drawdown.linkToInvestment")}
            name={investmentFieldName}
            isDisabled={
              useTypeId !== CreditFacilityPrincipalUseTypeEnum.investment
            }
            hideMessages={true}
            controlSx={{
              width: "300px",
            }}
          >
            {investmentData.map(
              ({ id, legal_name }) =>
                id && (
                  <SelectOption value={id} key={id}>
                    {legal_name}
                  </SelectOption>
                ),
            )}
          </FormSelect>
        </FormErrorTooltip>
      </Box>
      {showTransactions ? (
        <Box flex="1">
          <FormErrorTooltip name={txFieldName} wrapperProps={{ flexGrow: 1 }}>
            <FormSelect
              title={t("capitalCall:transactionCode")}
              name={txFieldName}
              controlSx={{
                width: "250px",
              }}
              defaultValue={defaultTransactionCode}
            >
              {transactionData
                .filter((tx) => tx.classification == useTypeId)
                .map(({ transaction_code_id, rich_name }) => (
                  <SelectOption
                    value={transaction_code_id}
                    key={transaction_code_id}
                  >
                    {rich_name}
                  </SelectOption>
                ))}
            </FormSelect>
          </FormErrorTooltip>
        </Box>
      ) : (
        <FormHiddenInput name={txFieldName} value={defaultTransactionCode} />
      )}
    </HStack>
  );
};
